import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { useLocalStorageRef } from '../hooks/use-local-storage-ref';
import { createFetcher } from '../lib/fetcher';
import { createAuthHeaders } from '../utils/auth';

type AuthStatus = 'authorized' | 'unauthorized' | 'pending';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  accessToken: string;
}

interface SignUpRequest {
  email: string;
  password: string;
}

interface SignUpResponse {
  accessToken: string;
}

interface UserInfoResponse {
  id: string;
  sessionId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
}

interface AuthContextData {
  loading: boolean;
  user: UserInfoResponse | null;
  login(email: string, password: string): Promise<void>;
  logout(): void;
  signUp(email: string, password: string): Promise<void>;
}

interface Props {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextData>({
  loading: false,
  user: null,
  async login() {},
  async logout() {},
  async signUp() {}
});

export function AuthContextProvider({ children }: Props) {
  const tokenRef = useLocalStorageRef('accessToken');
  const [user, setUser] = useState<UserInfoResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);

      const loginResult = await postAuthLoginFetcher({ email, password });
      if (!loginResult.ok) {
        console.error(`failed to login: ${loginResult.error}`);
        setLoading(false);
        throw new Error(loginResult.error);
      }

      tokenRef.update(loginResult.result.accessToken);

      const userResult = await getAuthUserStateFetcher(null, {
        headers: createAuthHeaders(tokenRef.value())
      });

      setLoading(false);

      if (!userResult.ok) {
        console.error(`failed to get user info using token: ${userResult.error}`);
        throw new Error(userResult.error);
      }

      setUser(userResult.result);
    },
    [tokenRef]
  );

  const logout = useCallback(async () => {
    const result = await putAuthLogoutFetcher(null, {
      headers: createAuthHeaders(tokenRef.value())
    });

    tokenRef.update(null);
    setUser(null);

    if (!result.ok) {
      console.error(`failed to logout: ${result.error}`);
      throw new Error(result.error);
    }
  }, [tokenRef]);

  const signUp = useCallback(
    async (email: string, password: string) => {
      setLoading(true);

      const signUpResult = await postAuthSignUpFetcher({ email, password });

      if (!signUpResult.ok) {
        console.error(`failed to sign up: ${signUpResult.error}`);
        setLoading(false);
        throw new Error(signUpResult.error);
      }

      tokenRef.update(signUpResult.result.accessToken);

      const userResult = await getAuthUserStateFetcher(null, {
        headers: createAuthHeaders(tokenRef.value())
      });

      setLoading(false);

      if (!userResult.ok) {
        console.error(`failed to get user info using token: ${userResult.error}`);
        throw new Error(userResult.error);
      }

      setUser(userResult.result);
    },
    [tokenRef]
  );

  const getUserInfo = useCallback(async () => {
    setLoading(true);

    const result = await getAuthUserStateFetcher(null, {
      headers: createAuthHeaders(tokenRef.value())
    });

    setLoading(false);

    if (!result.ok) {
      console.error(`failed to get user info using token: ${result.error}`);
      throw new Error(result.error);
    }

    setUser(result.result);
  }, [tokenRef]);

  useEffect(() => {
    if (tokenRef.value()) {
      getUserInfo().catch(() => null);
    } else {
      setLoading(false);
    }
  }, [tokenRef, getUserInfo]);

  return (
    <AuthContext.Provider value={{ loading, user, login, logout, signUp }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextData & { status: AuthStatus } {
  const data = useContext(AuthContext);
  const { loading, user } = data;
  const [status, setStatus] = useState<AuthStatus>('pending');

  useEffect(() => {
    setStatus(loading ? 'pending' : user ? 'authorized' : 'unauthorized');
  }, [loading, user]);

  return { status, ...data };
}

const getAuthUserStateFetcher = createFetcher<null, UserInfoResponse>({
  method: 'get',
  baseUrl: '/api/auth/user'
});

const postAuthLoginFetcher = createFetcher<LoginRequest, LoginResponse>({
  method: 'post',
  baseUrl: '/api/auth/login'
});

const putAuthLogoutFetcher = createFetcher<null, null>({
  method: 'put',
  baseUrl: '/api/auth/logout',
  silent: true
});

const postAuthSignUpFetcher = createFetcher<SignUpRequest, SignUpResponse>({
  method: 'post',
  baseUrl: '/api/auth/sign-up'
});
